<!-- 
      客戶詳細資料 modal
 -->
<template>
  <ModalOpener v-model="isOpened">
    <template #dialog="{ props }">
      <v-dialog
        v-bind="props"
        fullscreen
        persistent no-click-animation
        :scrim="false"
        transition="dialog-bottom-transition">
        <template v-slot:activator="{ props }">

        </template>
        <v-card class="modal-card">
          <!-- Header -->
          <v-toolbar :height="68" color="white" class="top-toolbar">
            <div class="wrap d-flex align-center justify-space-between">
              <h5 class="bold">客戶資料</h5>
              <div class="btns">
                <v-btn variant="text" class="font-weight-bold" @click="close">返回</v-btn>
              </div>
            </div>
          </v-toolbar>
          <!-- Form -->
          <div class="flex-1-1" style="overflow-y: scroll;">
            <div class="form-frame" ref="formFrame" v-if="account">
              <!-- Holder -->
              <DealingClientInfo
                :holder="account.holder"
                :accountType="account.account_type"
                :referenceData="subscriberReferenceData"
                :isInitial="account.initial"
                :currentMonth="account.formal_submitted_at"
                comparisonContext="client"></DealingClientInfo>
              <template v-if="account.account_type === AccountType.Joint">
                <v-expansion-panels v-for="(shareholder, index) in account.shareholders" class="mt-12">
                  <v-expansion-panel>
                    <v-expansion-panel-title expand-icon="arrow_drop_down" collapse-icon="arrow_drop_up">
                      <span class="font-subtitle bold">共同股份持有人:</span>
                      <span :data-count="index + 1" class="square mr-1 ml-2" />
                      <span class="font-subtitle bold">
                        {{ shareholder.profile.personal_name.last_name }}
                        {{ shareholder.profile.personal_name.first_name }}
                      </span>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text class="py-8" eager>
                      <!-- Shareholder -->
                      <DealingClientInfo
                        :holder="shareholder"
                        :accountType="account.account_type"
                        :referenceData="shareholderReferenceData(index)"
                        :isInitial="account.initial"
                        :currentMonth="account.formal_submitted_at"
                        comparisonContext="client"></DealingClientInfo>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>
              </template>

            </div>
          </div>

        </v-card>
      </v-dialog>
    </template>
  </ModalOpener>
</template>

<script setup lang="ts">
import { loadAccountV3, IHolderAccount, AccountType } from '@/api/dealings'
import { accountReferenceDataOwner } from '@/lib/accountReferenceDataImport'

const isOpened = ref(false)
const accountId = ref<number>()
const account = ref<IHolderAccount|null>()
const referenceData = computed(() => account.value?.initial_data || [])
const jointAccount = computed(() => account.value?.account_type === AccountType.Joint)
const shareholderCount = computed(() => account.value?.shareholders?.length || 0)
const subscriberReferenceData = computed(() => referenceData.value.filter(row => ['subscriber', 'unknown'].includes(referenceDataOwner(row))))

function shareholderReferenceData(index: number) {
  return referenceData.value.filter(row => [
    `shareholder:${index + 1}`,
    'unknown',
  ].includes(referenceDataOwner(row)))
}

function referenceDataOwner(row: NonNullable<IHolderAccount['initial_data']>[number]) {
  return accountReferenceDataOwner(row.field, row.ord, jointAccount.value, shareholderCount.value)
}

function open(account_id: number) {
  isOpened.value = true
  accountId.value = account_id
}
function close() {
  isOpened.value = false
}

watch(accountId, () => {
  account.value = null
  if (accountId.value) {
    useLoader(loadAccountV3({ account_id: accountId.value })).then(({ data }) => {
      account.value = data.value.account
    })
  }
})

defineExpose({
  open,
  close,
})

</script>

<style lang="stylus" scoped>
.modal-card
  background $gray-lighten-5

.top-toolbar
  background white
  box-shadow 0px 1px 8px 0px rgba(0, 0, 0, 0.08), 0px 2px 4px 0px rgba(0, 0, 0, 0.08)
  z-index 20
  .wrap
    width 100%
    max-width 1284px
    margin auto
    padding 0 42px
    +under(md)
      padding 0 12px

.form-frame
  width 100%
  max-width 1284px
  margin auto
  padding 48px 42px 96px
  +under(md)
    padding 48px 12px 96px
  :deep .info-list
    background #fff
    .list-head:hover
      background initial

.unknown-reference-data
  background #fff
  border 1px solid #E0E0E0
  border-radius 4px
  overflow hidden
  .list-head
    padding 16px 12px
    border-bottom 1px solid #E0E0E0
  .body-row
    display flex
    min-height 48px
    &:nth-child(odd)
      background $gray-lighten-5
  .field
    display flex
    align-items center
    padding 8px 12px
  .title
    flex 0 0 200px
  .content
    border-left 1px solid #E0E0E0

// 數量小方塊
.square
  position relative
  width 20px
  height 20px
  background-color $black-0-38
  border 2px solid #fff
  &:after
    content attr(data-count)
    display block
    position absolute
    color #fff
    font-size 12px
    top 50%
    left 50%
    transform translate(-50%, -50%)
</style>
